import React, { useState, useEffect, useCallback, useRef } from "react";
import { Challenge, challengeService } from "../services/challengeService";
import { connectWebSocket, disconnectWebSocket } from "../utils/websocket";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import {
  Avatar,
  Modal,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import TablePagination from "@mui/material/TablePagination";
import HoldingModal from "./HoldingModal";

interface ChallengeDetailProps {
  challengeData: Challenge;
  onBack: () => void;
  onEdit: () => void;
  onDelete: () => void;
}
type ChallengeParticipant = {
  user_id: string;
  full_name: string;
  rank: number;
  portfolio_value: number;
  total_spent: number;
  total_profit_loss: number;
  profit_loss_percentage: number;
  last_updated: string;
  holdings: any;
};
export default function ChallengeDetail({
  challengeData,
  onBack,
  onEdit,
  onDelete,
}: ChallengeDetailProps) {
  const [activeTab, setActiveTab] = useState("overview");
  const [participants, setParticipants] = useState<any[]>([]);
  const [participantsLoading, setParticipantsLoading] = useState(false);
  const [participantsError, setParticipantsError] = useState<string | null>(
    null
  );
  const [leaderboardLoading, setLeaderBoardLoading] = useState(false);
  const [data, setData] = useState<ChallengeParticipant[]>([]);
  const [dataVals, setDataVals] = useState<any>({});
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [holdingModal, setHoldingModal] = useState(false);
  const [selectedHolding, setSelectedHolding] = useState(null);
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1); // Reset to first page
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(amount);
  };

  // Helper function to safely render challenge properties
  const safeRender = (value: any): string => {
    if (value === null || value === undefined) return "N/A";
    if (typeof value === "object") return JSON.stringify(value);
    return String(value);
  };
  const tabs = [
    { id: "overview", name: "Overview", icon: "📊" },
    { id: "trading_rules", name: "Trading Rules", icon: "📋" },
    { id: "prize_distribution", name: "Prize Distribution", icon: "🏆" },
    { id: "participants", name: "Participants", icon: "👥" },
    { id: "other_details", name: "Other Details", icon: "⚙️" },
    { id: "leaderboard", name: "LeaderBoard", icon: "🥇" },
  ];
  // WebSocket connection state tracking
  const [wsConnectionState, setWsConnectionState] = useState<
    "disconnected" | "connecting" | "connected" | "error"
  >("disconnected");
  const wsConnectedRef = useRef(false);
  const hasFetchedOnceRef = useRef(false);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Optimized WebSocket handlers using useCallback to prevent stale closures
  const handleRowUpdate = useCallback((index: number, newData: any) => {
    setData((prevData) => {
      if (!prevData || index < 0 || index >= prevData.length) {
        console.warn(
          `Invalid row update index: ${index}, data length: ${
            prevData?.length || 0
          }`
        );
        return prevData;
      }
      const updated = [...prevData];
      updated[index] = { ...updated[index], ...newData };
      return updated;
    });
  }, []);

  const handleUserEnter = useCallback(
    (index: number, user: any) => {
      setData((prev) => {
        if (!prev || !user) {
          console.warn("Invalid user enter data:", {
            index,
            user,
            prevLength: prev?.length,
          });
          return prev || [];
        }
        const updated = [...prev];
        // Check if user already exists to prevent duplicates
        const existingIndex = updated.findIndex(
          (p) => p.user_id === user.user_id
        );
        if (existingIndex !== -1) {
          // Update existing user instead of adding duplicate
          updated[existingIndex] = { ...updated[existingIndex], ...user };
          return updated;
        }
        // Insert new user at specified index
        updated.splice(Math.max(0, Math.min(index, updated.length)), 0, user);
        return updated.slice(0, rowsPerPage);
      });
    },
    [rowsPerPage]
  );

  const handleUserLeave = useCallback((userId: string) => {
    setData((prev) => {
      if (!prev || !userId) {
        console.warn("Invalid user leave data:", {
          userId,
          prevLength: prev?.length,
        });
        return prev || [];
      }
      const filtered = prev.filter((p) => p.user_id !== userId);
      console.log(
        `User ${userId} left. Removed from UI. Before: ${prev.length}, After: ${filtered.length}`
      );
      return filtered;
    });
  }, []);

  const handlePriceUpdate = useCallback((symbol: string, price: number) => {
    console.log(`Live price update: ${symbol} = ₹${price}`);
    // Update portfolio values for users holding this symbol
    setData((prevData) => {
      if (!prevData) return prevData;
      return prevData.map((participant) => {
        if (participant.holdings && Array.isArray(participant.holdings)) {
          const hasSymbol = participant.holdings.some(
            (holding: any) =>
              holding.symbol === symbol || holding.stock_symbol === symbol
          );
          if (hasSymbol) {
            // Trigger a re-render by creating new object
            return { ...participant, last_updated: new Date().toISOString() };
          }
        }
        return participant;
      });
    });
  }, []);

  const handleConnectionChange = useCallback(
    (state: "disconnected" | "connecting" | "connected" | "error") => {
      setWsConnectionState(state);
      wsConnectedRef.current = state === "connected";
    },
    []
  );

  // WebSocket connection management
  const connectToWebSocket = useCallback(async () => {
    if (activeTab !== "leaderboard" || wsConnectedRef.current) {
      return;
    }

    try {
      // Fetch initial data only once
      if (!hasFetchedOnceRef.current) {
        await fetchLeaderboard();
        hasFetchedOnceRef.current = true;
      }
      if (challengeData?.status === "Completed") {
        console.log("Challenge completed, skipping WebSocket connection");
        return;
      }
      // Clear any existing reconnection timeout
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      // Disconnect any existing connection before creating new one
      disconnectWebSocket();

      // Create new WebSocket connection
      connectWebSocket(challengeData.id, page, rowsPerPage, {
        onRowUpdate: handleRowUpdate,
        onUserEnter: handleUserEnter,
        onUserLeave: handleUserLeave,
        onPriceUpdate: handlePriceUpdate,
        onConnectionChange: handleConnectionChange,
      });

      wsConnectedRef.current = true;
      console.log("WebSocket connected successfully");

      // Set up automatic reconnection (every 60 seconds)
      reconnectTimeoutRef.current = setTimeout(() => {
        if (activeTab === "leaderboard") {
          wsConnectedRef.current = false;
          connectToWebSocket();
        }
      }, 60000);
    } catch (error) {
      console.error("Error connecting to WebSocket:", error);
      wsConnectedRef.current = false;

      // Retry connection after 5 seconds on error
      reconnectTimeoutRef.current = setTimeout(() => {
        if (activeTab === "leaderboard") {
          connectToWebSocket();
        }
      }, 5000);
    }
  }, [
    activeTab,
    challengeData.id,
    page,
    rowsPerPage,
    handleRowUpdate,
    handleUserEnter,
    handleUserLeave,
    handlePriceUpdate,
    handleConnectionChange,
  ]);
  // Main WebSocket effect
  useEffect(() => {
    if (activeTab === "leaderboard") {
      connectToWebSocket();
    } else {
      // Clean up when leaving leaderboard tab
      disconnectWebSocket();
      wsConnectedRef.current = false;
      hasFetchedOnceRef.current = false;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    }

    return () => {
      disconnectWebSocket();
      wsConnectedRef.current = false;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    };
  }, [activeTab, challengeData.id, page, rowsPerPage, connectToWebSocket]);

  const fetchParticipants = async () => {
    if (activeTab !== "participants") return;

    try {
      setParticipantsLoading(true);
      setParticipantsError(null);

      const data = await challengeService.getChallengeParticipants(
        challengeData.id,
        1,
        100
      );

      setParticipants(data.participants || []);
      setDataVals(data);
      setData(data.participants || []);
    } catch (error) {
      console.error("Error fetching participants:", error);
      setParticipantsError(
        error instanceof Error ? error.message : "Failed to fetch participants"
      );
    } finally {
      setParticipantsLoading(false);
    }
  };
  const fetchLeaderboard = async () => {
    if (activeTab !== "leaderboard") return;

    try {
      setLeaderBoardLoading(true);
      const res = await challengeService.getLeaderBoard(
        challengeData.id,
        page,
        rowsPerPage
      );
      setData(res.participants || []);
      setTotalItems(res.total_participants || 0);
    } catch (error) {
      console.error("Failed to fetch leaderboard", error);
    } finally {
      setLeaderBoardLoading(false);
    }
  };
  // useEffect(() => {
  //   if (activeTab === "leaderboard") {
  //     fetchLeaderboard();
  //   }
  // }, [activeTab, challengeData.id,page,rowsPerPage]);
  useEffect(() => {
    if (activeTab === "participants") {
      fetchParticipants();
    }
  }, [activeTab, challengeData.id]);
  const handleOpenModal = (holding: any) => {
    setSelectedHolding(holding);
    setHoldingModal(true);
  };
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <button
                onClick={onBack}
                className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4 transition-colors duration-200"
              >
                <svg
                  className="w-4 h-4 mr-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 19l-7-7m0 0l7-7m-7 7h18"
                  />
                </svg>
                Back to Challenges
              </button>
              <div className="flex items-center space-x-4">
                <div className="flex-shrink-0 h-16 w-16">
                  <div className="h-16 w-16 rounded-full bg-indigo-100 flex items-center justify-center">
                    <svg
                      className="w-8 h-8 text-indigo-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                      />
                    </svg>
                  </div>
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">
                    {challengeData.name}
                  </h1>
                  <p className="mt-2 text-sm text-gray-600">
                    {challengeData.description}
                  </p>
                  <div className="mt-2 flex items-center space-x-4">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        challengeData.status === "Live"
                          ? "bg-green-100 text-green-800"
                          : challengeData.status === "Upcoming"
                          ? "bg-blue-100 text-blue-800"
                          : challengeData.status === "Completed"
                          ? "bg-gray-100 text-gray-800"
                          : challengeData.status === "Cancelled"
                          ? "bg-red-100 text-red-800"
                          : "bg-yellow-100 text-yellow-800"
                      }`}
                    >
                      {challengeData.status}
                    </span>
                    <span className="text-sm text-gray-500">
                      Created {formatDate(challengeData.created_at)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex space-x-3">
              {challengeData.status === "Draft" ||
              challengeData.status === "Upcoming" ? (
                <button
                  onClick={onEdit}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200"
                >
                  <svg
                    className="w-4 h-4 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                    />
                  </svg>
                  Edit
                </button>
              ) : (
                <div className="text-sm text-gray-500 italic">
                  Cannot edit {challengeData.status.toLowerCase()} challenges
                </div>
              )}
              <button
                onClick={onDelete}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors duration-200"
              >
                <svg
                  className="w-4 h-4 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
                Delete
              </button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-blue-500 rounded-md flex items-center justify-center">
                    <svg
                      className="w-5 h-5 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"
                      />
                    </svg>
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Entry Fee
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {challengeData.entry_fee === 0
                        ? "Free"
                        : formatCurrency(challengeData.entry_fee)}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-green-500 rounded-md flex items-center justify-center">
                    <svg
                      className="w-5 h-5 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"
                      />
                    </svg>
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Prize Pool
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {formatCurrency(challengeData.prize_pool)}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-yellow-500 rounded-md flex items-center justify-center">
                    <svg
                      className="w-5 h-5 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
                      />
                    </svg>
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Participants
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {challengeData.max_participants || 0}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-purple-500 rounded-md flex items-center justify-center">
                    <svg
                      className="w-5 h-5 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Duration
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {(() => {
                        if (
                          !challengeData.start_date ||
                          !challengeData.end_date
                        ) {
                          return "N/A";
                        }
                        const startDate = new Date(challengeData.start_date);
                        const endDate = new Date(challengeData.end_date);
                        const timeDiff =
                          endDate.getTime() - startDate.getTime();
                        const daysDiff =
                          Math.ceil(timeDiff / (1000 * 3600 * 24)) + 1; // +1 to include both dates
                        return `${daysDiff} day${daysDiff !== 1 ? "s" : ""}`;
                      })()}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white shadow rounded-lg">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                    activeTab === tab.id
                      ? "border-indigo-500 text-indigo-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  <span className="mr-2">{tab.icon}</span>
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {/* Overview Tab */}
            {activeTab === "overview" && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                      Challenge Information
                    </h3>
                    <dl className="space-y-3">
                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          Name
                        </dt>
                        <dd className="text-sm text-gray-900">
                          {challengeData.name}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          Description
                        </dt>
                        <dd className="text-sm text-gray-900">
                          {safeRender(challengeData.description)}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          Type
                        </dt>
                        <dd className="text-sm text-gray-900">
                          {safeRender(challengeData.type)}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          Category
                        </dt>
                        <dd className="text-sm text-gray-900">
                          {safeRender(challengeData.category)}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          Experience Level
                        </dt>
                        <dd className="text-sm text-gray-900">
                          {safeRender(challengeData.experience_level)}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          Risk Appetite
                        </dt>
                        <dd className="text-sm text-gray-900">
                          {safeRender(challengeData.risk_appetite)}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          Is Guaranteed
                        </dt>
                        <dd className="text-sm text-gray-900">
                          {challengeData.is_guaranteed ? "Yes" : "No"}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          Status
                        </dt>
                        <dd className="text-sm text-gray-900">
                          {safeRender(challengeData.status)}
                        </dd>
                      </div>
                    </dl>
                  </div>

                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                      Timeline
                    </h3>
                    <dl className="space-y-3">
                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          Start Date
                        </dt>
                        <dd className="text-sm text-gray-900">
                          {challengeData.start_date
                            ? formatDate(challengeData.start_date)
                            : "Not set"}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          End Date
                        </dt>
                        <dd className="text-sm text-gray-900">
                          {challengeData.end_date
                            ? formatDate(challengeData.end_date)
                            : "Not set"}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          Duration
                        </dt>
                        <dd className="text-sm text-gray-900">
                          {(() => {
                            if (
                              !challengeData.start_date ||
                              !challengeData.end_date
                            ) {
                              return "N/A";
                            }
                            const startDate = new Date(
                              challengeData.start_date
                            );
                            const endDate = new Date(challengeData.end_date);
                            const timeDiff =
                              endDate.getTime() - startDate.getTime();
                            const daysDiff =
                              Math.ceil(timeDiff / (1000 * 3600 * 24)) + 1; // +1 to include both dates
                            return `${daysDiff} day${
                              daysDiff !== 1 ? "s" : ""
                            }`;
                          })()}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          Created At
                        </dt>
                        <dd className="text-sm text-gray-900">
                          {formatDate(challengeData.created_at)}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          Updated At
                        </dt>
                        <dd className="text-sm text-gray-900">
                          {formatDate(challengeData.updated_at)}
                        </dd>
                      </div>
                    </dl>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                      Financial Details
                    </h3>
                    <dl className="space-y-3">
                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          Entry Fee
                        </dt>
                        <dd className="text-sm text-gray-900">
                          {challengeData.entry_fee === 0
                            ? "Free"
                            : formatCurrency(challengeData.entry_fee)}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          Prize Pool
                        </dt>
                        <dd className="text-sm text-gray-900">
                          {formatCurrency(challengeData.prize_pool)}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          Max Participants
                        </dt>
                        <dd className="text-sm text-gray-900">
                          {challengeData.max_participants || 0}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          Current Participants
                        </dt>
                        <dd className="text-sm text-gray-900">
                          {challengeData.current_participants
                            ? challengeData.current_participants
                            : dataVals.total_participants
                            ? dataVals.total_participants
                            : 0}
                        </dd>
                      </div>
                    </dl>
                  </div>

                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                      Quick Stats
                    </h3>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <dt className="text-sm font-medium text-gray-500">
                            Days Remaining
                          </dt>
                          <dd className="text-lg font-medium text-gray-900">
                            {challengeData.end_date
                              ? Math.max(
                                  0,
                                  Math.ceil(
                                    (new Date(
                                      challengeData.end_date
                                    ).getTime() -
                                      new Date().getTime()) /
                                      (1000 * 60 * 60 * 24)
                                  )
                                )
                              : "N/A"}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-sm font-medium text-gray-500">
                            Fill Rate
                          </dt>
                          <dd className="text-lg font-medium text-gray-900">
                            {challengeData.max_participants
                              ? `${Math.round(
                                  ((challengeData.current_participants || 0) /
                                    challengeData.max_participants) *
                                    100
                                )}%`
                              : "N/A"}
                          </dd>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Trading Rules Tab */}
            {activeTab === "trading_rules" && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-blue-800 mb-2">
                      Virtual Capital
                    </h4>
                    <p className="text-2xl font-bold text-blue-900">
                      {challengeData.virtual_capital
                        ? formatCurrency(challengeData.virtual_capital)
                        : "N/A"}
                    </p>
                  </div>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-red-800 mb-2">
                      Brokerage Fee
                    </h4>
                    <p className="text-2xl font-bold text-red-900">
                      {challengeData.brokerage_fee
                        ? `${(challengeData.brokerage_fee * 100)?.toFixed(2)}%`
                        : "N/A"}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                      Stock Universe
                    </h3>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-sm text-gray-700">
                        {Array.isArray(challengeData.stock_universe_ids) &&
                        challengeData.stock_universe_ids.length > 0
                          ? challengeData.stock_universe || "All Stocks allowed"
                          : "All Stocks allowed"}
                      </p>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                      Trading Restrictions
                    </h3>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500">
                          Max Open Positions
                        </span>
                        <span className="text-sm font-medium text-gray-900">
                          {challengeData.trading_restrictions
                            ?.max_open_positions || 0}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500">
                          Max Capital per Stock
                        </span>
                        <span className="text-sm font-medium text-gray-900">
                          {challengeData.trading_restrictions
                            ?.max_capital_per_stock || "N/A"}
                          %
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500">
                          Allow Short Selling
                        </span>
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            challengeData.trading_restrictions
                              ?.allow_short_selling
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {challengeData.trading_restrictions
                            ?.allow_short_selling
                            ? "Allowed"
                            : "Not Allowed"}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500">
                          Intraday Only
                        </span>
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            challengeData.trading_restrictions?.intraday_only
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {challengeData.trading_restrictions?.intraday_only
                            ? "Yes"
                            : "No"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                      Allowed Order Types
                    </h3>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500">
                          Market Orders
                        </span>
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Enabled
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500">
                          Limit Orders
                        </span>
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            challengeData.allowed_order_types?.limit_orders
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {challengeData.allowed_order_types?.limit_orders
                            ? "Allowed"
                            : "Not Allowed"}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500">
                          Stop-Loss Orders
                        </span>
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            challengeData.allowed_order_types?.stop_loss_orders
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {challengeData.allowed_order_types?.stop_loss_orders
                            ? "Allowed"
                            : "Not Allowed"}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                      Trading Hours
                    </h3>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500">
                          Start Time
                        </span>
                        <span className="text-sm font-medium text-gray-900">
                          {challengeData.trading_restrictions?.trading_hours
                            ?.start || "9:15 AM"}
                        </span>
                      </div>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-sm text-gray-500">End Time</span>
                        <span className="text-sm font-medium text-gray-900">
                          {challengeData.trading_restrictions?.trading_hours
                            ?.end || "3:30 PM"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Prize Distribution Tab */}
            {activeTab === "prize_distribution" && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg
                          className="h-5 w-5 text-yellow-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"
                          />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-yellow-800">
                          Prize Pool
                        </h3>
                        <div className="mt-2 text-sm text-yellow-700">
                          <p>
                            Total Prize Pool:{" "}
                            {formatCurrency(challengeData.prize_pool)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg
                          className="h-5 w-5 text-blue-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                          />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-blue-800">
                          Challenge Type
                        </h3>
                        <div className="mt-2 text-sm text-blue-700">
                          <p>{safeRender(challengeData.challenge_type)}</p>
                          {challengeData.challenge_type === "Guaranteed" && (
                            <p>
                              Guaranteed Prize Pool:{" "}
                              {formatCurrency(
                                challengeData.guaranteed_prize_pool || 0
                              )}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  {/* {(challengeData.prize_distribution || []).map(
                    (prize, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-4 border border-gray-200 rounded-lg"
                      >
                        <div className="flex items-center space-x-4">
                          <div
                            className={`w-12 h-12 rounded-full flex items-center justify-center ${
                              index === 0
                                ? "bg-yellow-100"
                                : index === 1
                                ? "bg-gray-100"
                                : index === 2
                                ? "bg-orange-100"
                                : "bg-blue-100"
                            }`}
                          >
                            <span
                              className={`text-lg font-bold ${
                                index === 0
                                  ? "text-yellow-600"
                                  : index === 1
                                  ? "text-gray-600"
                                  : index === 2
                                  ? "text-orange-600"
                                  : "text-blue-600"
                              }`}
                            >
                              {prize.rank}
                            </span>
                          </div>
                          <div>
                            <h4 className="text-sm font-medium text-gray-900">
                              {index === 0
                                ? "🥇 1st Place"
                                : index === 1
                                ? "🥈 2nd Place"
                                : index === 2
                                ? "🥉 3rd Place"
                                : `${prize.rank}th Place`}
                            </h4>
                            <p className="text-sm text-gray-500">
                              {prize.percentage}% of prize pool
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-gray-900">
                            {formatCurrency(
                              (prize.percentage / 100) *
                                challengeData.prize_pool
                            )}
                          </p>
                        </div>
                      </div>
                    )
                  )} */}
                  {(challengeData.prize_distribution || []).map(
                    (prize, index) => {
                      // subtract platform fee from the pool first:
                      const netPrizePool =
                        challengeData.prize_pool *
                        (1 - (challengeData.platform_stake || 0) / 100);

                      const prizeAmount =
                        (prize.percentage / 100) * netPrizePool;

                      return (
                        <div
                          key={index}
                          className="flex items-center justify-between p-4 border border-gray-200 rounded-lg"
                        >
                          <div className="flex items-center space-x-4">
                            <div
                              className={`w-12 h-12 rounded-full flex items-center justify-center ${
                                index === 0
                                  ? "bg-yellow-100"
                                  : index === 1
                                  ? "bg-gray-100"
                                  : index === 2
                                  ? "bg-orange-100"
                                  : "bg-blue-100"
                              }`}
                            >
                              <span
                                className={`text-lg font-bold ${
                                  index === 0
                                    ? "text-yellow-600"
                                    : index === 1
                                    ? "text-gray-600"
                                    : index === 2
                                    ? "text-orange-600"
                                    : "text-blue-600"
                                }`}
                              >
                                {prize.rank}
                              </span>
                            </div>
                            <div>
                              <h4 className="text-sm font-medium text-gray-900">
                                {index === 0
                                  ? "🥇 1st Place"
                                  : index === 1
                                  ? "🥈 2nd Place"
                                  : index === 2
                                  ? "🥉 3rd Place"
                                  : `${prize.rank}th Place`}
                              </h4>
                              <p className="text-sm text-gray-500">
                                {prize.percentage}% of prize pool (after{" "}
                                {challengeData.platform_stake}% fee)
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold text-gray-900">
                              {formatCurrency(prizeAmount)}
                            </p>
                          </div>
                        </div>
                      );
                    }
                  )}
                </div>

                {/* Business Metrics for Guaranteed Challenges */}
                {challengeData.is_guaranteed && (
                  <div className="mt-8 bg-gray-50 border border-gray-200 rounded-lg p-6">
                    <div className="flex items-center space-x-2 mb-4">
                      <svg
                        className="h-5 w-5 text-gray-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                        />
                      </svg>
                      <h3 className="text-lg font-medium text-gray-900">
                        Business Metrics
                      </h3>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                      <div className="bg-white border border-gray-300 rounded-lg p-4">
                        <h4 className="text-sm font-medium text-gray-500 mb-2">
                          Breakeven Participants
                        </h4>
                        <p className="text-2xl font-bold text-blue-600">
                          {challengeData.entry_fee > 0
                            ? Math.ceil(
                                challengeData.prize_pool /
                                  (challengeData.entry_fee * 0.9)
                              ).toLocaleString()
                            : "N/A"}
                        </p>
                        <p className="text-sm text-gray-500 mt-1">
                          Participants needed to break even
                        </p>
                      </div>

                      <div className="bg-white border border-gray-300 rounded-lg p-4">
                        <h4 className="text-sm font-medium text-gray-500 mb-2">
                          Current Profit/Loss
                        </h4>
                        <div className="flex items-center space-x-2">
                          {(() => {
                            const currentParticipants =
                              challengeData.current_participants || 0;
                            const revenue =
                              currentParticipants *
                              challengeData.entry_fee *
                              0.9;
                            const profitLoss =
                              revenue - challengeData.prize_pool;
                            const isProfit = profitLoss >= 0;

                            return (
                              <>
                                <svg
                                  className={`h-5 w-5 ${
                                    isProfit ? "text-green-500" : "text-red-500"
                                  }`}
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d={
                                      isProfit
                                        ? "M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                                        : "M13 17h8m0 0V9m0 8l-8-8-4 4-6-6"
                                    }
                                  />
                                </svg>
                                <p
                                  className={`text-2xl font-bold ${
                                    isProfit ? "text-green-600" : "text-red-600"
                                  }`}
                                >
                                  {formatCurrency(Math.abs(profitLoss))}{" "}
                                  {isProfit ? "Profit" : "Loss"}
                                </p>
                              </>
                            );
                          })()}
                        </div>
                        <p className="text-sm text-gray-500 mt-1">
                          Based on {challengeData.current_participants || 0}{" "}
                          participants
                        </p>
                      </div>
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-blue-800 mb-2">
                        Formula Explanation
                      </h4>
                      <div className="space-y-1 text-sm text-blue-700">
                        <p>
                          • Breakeven = ⌈Guaranteed Prize Pool ÷ (Entry Fee ×
                          0.9)⌉
                        </p>
                        <p>
                          • Profit/Loss = (Current Participants × Entry Fee ×
                          0.9) - Guaranteed Prize Pool
                        </p>
                        <p className="text-xs text-blue-600 mt-2">
                          Note: 10% platform rake is deducted from entry fees
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Participants Tab */}
            {activeTab === "participants" && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg
                          className="h-5 w-5 text-blue-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
                          />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-blue-800">
                          Participant Information
                        </h3>
                        <div className="mt-2 text-sm text-blue-700">
                          <p>
                            Current Participants:{" "}
                            {challengeData.current_participants
                              ? challengeData.current_participants
                              : dataVals.total_participants
                              ? dataVals.total_participants
                              : 0}
                          </p>
                          <p>
                            Max Participants:{" "}
                            {challengeData.max_participants || 0}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg
                          className="h-5 w-5 text-green-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                          />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-green-800">
                          Fill Rate
                        </h3>
                        <div className="mt-2 text-sm text-green-700">
                          <p className="text-2xl font-bold">
                            {challengeData.max_participants
                              ? `${Math.round(
                                  ((challengeData.current_participants || 0) /
                                    challengeData.max_participants) *
                                    100
                                )}%`
                              : "N/A"}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {participantsLoading ? (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
                    <p className="mt-2 text-sm text-gray-500">
                      Loading participants...
                    </p>
                  </div>
                ) : participantsError ? (
                  <div className="text-center py-12">
                    <svg
                      className="mx-auto h-12 w-12 text-red-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                      />
                    </svg>
                    <h3 className="mt-2 text-sm font-medium text-gray-900">
                      Error loading participants
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                      {participantsError}
                    </p>
                  </div>
                ) : participants.length === 0 ? (
                  <div className="text-center py-12">
                    <svg
                      className="mx-auto h-12 w-12 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
                      />
                    </svg>
                    <h3 className="mt-2 text-sm font-medium text-gray-900">
                      No participants yet
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Participant list will appear here once users join the
                      challenge.
                    </p>
                  </div>
                ) : (
                  <div className="bg-white shadow overflow-hidden sm:rounded-md">
                    <ul className="divide-y divide-gray-200">
                      {participants.map((participant, index) => (
                        <li key={participant.id || index} className="px-6 py-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center">
                              <div className="flex-shrink-0">
                                <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center">
                                  <span className="text-sm font-medium text-indigo-600">
                                    {participant.current_rank || index + 1}
                                  </span>
                                </div>
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">
                                  {participant.full_name ||
                                    participant.email ||
                                    "Unknown User"}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {participant.email}
                                </div>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-sm text-gray-900">
                                Joined: {formatDate(participant.joined_at)}
                              </div>
                              <div className="text-sm text-gray-500">
                                Entry Fee:{" "}
                                {formatCurrency(participant.entry_fee || 0)}
                              </div>
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Other Details Tab */}
            {activeTab === "other_details" && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                      Challenge Configuration
                    </h3>
                    <dl className="space-y-3">
                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          Duration Tag
                        </dt>
                        <dd className="text-sm text-gray-900">
                          {safeRender(challengeData.duration_tag)}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          Info Box Text
                        </dt>
                        <dd className="text-sm text-gray-900">
                          {safeRender(challengeData.info_box_text)}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          Created By
                        </dt>
                        <dd className="text-sm text-gray-900">
                          {safeRender(challengeData.created_by)}
                        </dd>
                      </div>
                    </dl>
                  </div>

                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                      Display Icons
                    </h3>
                    <dl className="space-y-3">
                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          First Prize Icon
                        </dt>
                        <dd className="text-sm text-gray-900">
                          {safeRender(challengeData.first_prize_icon)}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          Win % Icon
                        </dt>
                        <dd className="text-sm text-gray-900">
                          {safeRender(challengeData.win_percentage_icon)}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          Max Entries Icon
                        </dt>
                        <dd className="text-sm text-gray-900">
                          {safeRender(challengeData.max_entries_icon)}
                        </dd>
                      </div>
                    </dl>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-6">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                      Challenge Rules
                    </h3>
                    <div className="bg-gray-50 rounded-lg p-4">
                      {challengeData.rules ? (
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">
                          {safeRender(challengeData.rules)}
                        </p>
                      ) : (
                        <p className="text-sm text-gray-500 italic">
                          No rules specified
                        </p>
                      )}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                      Gameplay Rules
                    </h3>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="space-y-3 text-sm text-gray-700">
                        <div>
                          <strong>Order Types:</strong>
                          <span className="ml-2">
                            Market Orders (Always Enabled)
                            {challengeData.allowed_order_types?.limit_orders &&
                              ", Limit Orders"}
                            {challengeData.allowed_order_types
                              ?.stop_loss_orders && ", Stop-Loss Orders"}
                          </span>
                        </div>
                        <div>
                          <strong>Allowed Instruments:</strong>
                          <span className="ml-2">
                            {Array.isArray(challengeData.stock_universe_ids) &&
                            challengeData.stock_universe_ids.length > 0
                              ? challengeData.stock_universe || "All Stocks"
                              : "All Stocks"}
                          </span>
                        </div>
                        <div>
                          <strong>Max Open Positions:</strong>
                          <span className="ml-2">
                            {challengeData.trading_restrictions
                              ?.max_open_positions || 0}
                          </span>
                        </div>
                        <div>
                          <strong>Leverage:</strong>
                          <span className="ml-2">
                            {challengeData.virtual_capital
                              ? formatCurrency(challengeData.virtual_capital)
                              : "N/A"}
                          </span>
                        </div>
                        <div>
                          <strong>Short Selling:</strong>
                          <span className="ml-2">
                            {challengeData.trading_restrictions
                              ?.allow_short_selling
                              ? "Allowed"
                              : "Not Allowed"}
                          </span>
                        </div>
                        <div>
                          <strong>Intraday Trading:</strong>
                          <span className="ml-2">
                            {challengeData.trading_restrictions?.intraday_only
                              ? "Yes"
                              : "No"}
                          </span>
                        </div>
                        <div>
                          <strong>Brokerage:</strong>
                          <span className="ml-2">
                            {challengeData.brokerage_fee
                              ? `${(challengeData.brokerage_fee * 100)?.toFixed(
                                  2
                                )}%`
                              : "N/A"}
                          </span>
                        </div>
                        <div>
                          <strong>Trading Hours:</strong>
                          <span className="ml-2">
                            {challengeData.trading_restrictions?.trading_hours
                              ?.start || "9:15 AM"}{" "}
                            to{" "}
                            {challengeData.trading_restrictions?.trading_hours
                              ?.end || "3:30 PM"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "leaderboard" && (
              <div className="space-y-6">
                {/* WebSocket Connection Status */}
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Live Leaderboard
                  </h3>
                  {/* <div className="flex items-center space-x-2">
                    <div
                      className={`w-3 h-3 rounded-full ${
                        wsConnectionState === "connected"
                          ? "bg-green-500"
                          : wsConnectionState === "connecting"
                          ? "bg-yellow-500"
                          : wsConnectionState === "error"
                          ? "bg-red-500"
                          : "bg-gray-400"
                      }`}
                    ></div>
                    <span className="text-sm text-gray-600">
                      {wsConnectionState === "connected"
                        ? "Connected"
                        : wsConnectionState === "connecting"
                        ? "Connecting..."
                        : wsConnectionState === "error"
                        ? "Connection Error"
                        : "Disconnected"}
                    </span>
                  </div> */}
                </div>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <b>Full Name</b>
                        </TableCell>
                        <TableCell align="right">
                          <b>Portfolio Value (₹)</b>
                        </TableCell>
                        <TableCell align="right">
                          <b>Total Spent (₹)</b>
                        </TableCell>
                        <TableCell align="right">
                          <b>Profit/Loss (₹)</b>
                        </TableCell>
                        <TableCell align="right">
                          <b>Profit/Loss %</b>
                        </TableCell>
                        <TableCell align="right">
                          <b>Action</b>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {leaderboardLoading ? (
                        <TableRow>
                          <TableCell>Loading............</TableCell>
                        </TableRow>
                      ) : data?.length == 0 ? (
                        <TableRow>
                          <TableCell>No Data Found</TableCell>
                        </TableRow>
                      ) : (
                        data.map((row) => (
                          <TableRow key={row.user_id}>
                            <TableCell>
                              <div className="flex items-center">
                                <div className="flex-shrink-0">
                                  <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center">
                                    <Avatar
                                      // src={null}
                                      alt="User"
                                      sx={{
                                        width: 20,
                                        height: 20,
                                        fontSize: "14px",
                                        bgcolor: "#fff",
                                        color: "#000",
                                      }}
                                    >
                                      {row.full_name?.charAt(0).toUpperCase() ||
                                        "U"}
                                    </Avatar>
                                  </div>
                                </div>
                                <div className="ml-4">
                                  <div className="text-sm font-medium text-gray-900">
                                    {row.full_name || "Unknown User"}
                                  </div>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell align="right">
                              {row.portfolio_value?.toFixed(2)}
                            </TableCell>
                            <TableCell align="right">
                              {row.total_spent?.toFixed(2)}
                            </TableCell>
                            <TableCell align="right">
                              {row.total_profit_loss?.toFixed(2)}
                            </TableCell>
                            <TableCell align="right">
                              {row.profit_loss_percentage?.toFixed(2)}%
                            </TableCell>
                            <TableCell
                              align="right"
                              onClick={() => handleOpenModal(row.holdings)}
                            >
                              <RemoveRedEyeIcon />
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>

                  <TablePagination
                    component="div"
                    count={totalItems}
                    page={page - 1} // ✅ Adjust to 0-based
                    onPageChange={(e, newPage) => setPage(newPage + 1)} // ✅ Convert back to 1-based
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                  />
                </TableContainer>
                {selectedHolding && (
                  <HoldingModal
                    open={holdingModal}
                    onClose={() => setHoldingModal(false)}
                    data={selectedHolding}
                  />
                )}
                <div></div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
